<!--
  The Units / Volume chart, ported from CGS2022 originationInsightsChart.vue. One instance per
  measure, each with its own metric picker, as CGS has two independent charts.

  WHAT CAME ACROSS UNCHANGED
  - The four requests and their bodies (originationQueries.js chartSeriesQueries): this year
    and last year by month (GetInsightsData), and the two month-to-date overlays
    (GetInsightsChartMTDOverlayData). All four go again when the metric or the filters change.
  - The seven metrics, Purchased included, and which response list is drawn (units or volume).
  - The filters the chart honours: the filter bar's lists, not its dates. The chart is always
    this year against last year, as in CGS.

  WHAT CHANGED, ON PURPOSE
  - The look: the kit palette (last year grey, this year navy, the MTD lines gold and teal),
    light grid lines, legend at the bottom. CGS picked a random colour per pull that it then
    never used. That code is not ported.
  - The metric buttons are a drop-down, which fits a card header at phone width.
  - A failed request shows an error in the card. CGS left the series empty without saying so.
-->
<template>
  <InsightCard :title="unitsOrVolume" :subtitle="`${metric.name}: this year against last year, by month`" :loading="false">
    <template v-if="!metricName" #actions>
      <SearchSelect class="og-metric" :items="metricNames" :model-value="metric.name" aria-label="Metric" @change="onMetric" />
    </template>

    <div v-if="error" class="ins-banner ins-banner--error" role="alert">
      <i class="fa-solid fa-circle-exclamation"></i>
      <div><b>An error occurred.</b> {{ error }}</div>
    </div>
    <div class="og-chart" :aria-busy="loading ? 'true' : 'false'">
      <Chart :style="{ width: '100%', height: '320px' }" :transitions="false">
        <ChartLegend position="bottom" orientation="horizontal" />
        <ChartCategoryAxis>
          <ChartCategoryAxisItem :categories="categories" :major-grid-lines="{ visible: false }" :labels="{ rotation: 'auto' }">
            <ChartCategoryAxisTitle text="Months" />
          </ChartCategoryAxisItem>
        </ChartCategoryAxis>
        <ChartValueAxis>
          <ChartValueAxisItem :labels="{ format: isVolume ? 'c0' : 'n0' }" :major-grid-lines="{ color: '#eef1f7' }" />
        </ChartValueAxis>
        <ChartTooltip :shared="true" :format="isVolume ? 'c0' : 'n0'" />
        <ChartSeries>
          <ChartSeriesItem type="column" :name="`${ctitle} LY`" :color="colors.ly" :data-items="series[1]" :border="{ width: 0 }" :gap="0.6" :spacing="0.1" />
          <ChartSeriesItem type="column" :name="`${ctitle} TY`" :color="colors.ty" :data-items="series[0]" :border="{ width: 0 }" />
          <ChartSeriesItem type="line" :name="`${ctitle} TY MTD`" :color="colors.tyMtd" :width="3" :data-items="series[2]" />
          <ChartSeriesItem type="line" :name="`${ctitle} LY MTD`" :color="colors.lyMtd" :width="3" :data-items="series[3]" />
        </ChartSeries>
      </Chart>
      <div v-if="loading" class="og-chart__veil" aria-hidden="true"><div class="ins-skel"><span style="width:70%"></span><span style="width:50%"></span></div></div>
    </div>
  </InsightCard>
</template>

<script>
import 'hammerjs'
import {
  Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem, ChartCategoryAxisTitle,
  ChartValueAxis, ChartValueAxisItem, ChartLegend, ChartTooltip
} from '@progress/kendo-vue-charts'
import SearchSelect from '@/components/insightsKit/SearchSelect.vue'
import insightsApi from '@/api/insights.service.js'
import { InsightCard } from '@/components/insightsKit'
import { MIX_PALETTE } from '@/helpers/insights/insightMath.js'
import { CHART_METRICS, chartSeriesQueries } from './originationQueries.js'

export default {
  name: 'OriginationChart',
  components: {
    InsightCard, SearchSelect, Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem,
    ChartCategoryAxisTitle, ChartValueAxis, ChartValueAxisItem, ChartLegend, ChartTooltip
  },
  props: {
    unitsOrVolume: { type: String, required: true },
    // The filter lists from the filter bar (chartFiltersFromEvent). A new object reloads.
    filters: { type: Object, required: true },
    // Optional: a parent that draws its own metric picker (the Command Center Insights view) passes
    // the metric's name here. The card's own picker is hidden then, and the chart follows the prop.
    metricName: { type: String, default: '' }
  },
  emits: ['loading'],
  data() {
    return {
      categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      metric: CHART_METRICS.find(m => m.name === this.metricName) || CHART_METRICS[0],
      metricNames: CHART_METRICS.map(m => m.name),
      ctitle: (CHART_METRICS.find(m => m.name === this.metricName) || CHART_METRICS[0]).name,
      series: [[], [], [], []],
      loading: false,
      error: '',
      seq: 0,
      colors: { ly: MIX_PALETTE[4], ty: MIX_PALETTE[0], tyMtd: MIX_PALETTE[2], lyMtd: MIX_PALETTE[3] }
    }
  },
  computed: {
    isVolume() { return this.unitsOrVolume !== 'Units' }
  },
  watch: {
    filters() { this.getAllChartSeries() },
    metricName(name) { if (name) this.onMetric(name) }
  },
  mounted() { this.getAllChartSeries() },
  methods: {
    onMetric(name) {
      this.metric = CHART_METRICS.find(m => m.name === name) || CHART_METRICS[0]
      this.ctitle = this.metric.name
      this.getAllChartSeries()
    },
    // GetSeries1..4: each series is cleared, then filled from insightsGraph.units or .volume.
    async getAllChartSeries() {
      const seq = ++this.seq
      this.series = [[], [], [], []]
      this.loading = true
      this.error = ''
      this.$emit('loading', true)
      const prop = this.isVolume ? 'volume' : 'units'
      const results = await Promise.allSettled(chartSeriesQueries(this.metric.field, this.filters).map(q => insightsApi.insights(q.action, q.body)))
      if (seq !== this.seq) return
      const next = [[], [], [], []]
      let failed = ''
      results.forEach((r, i) => {
        const ok = r.status === 'fulfilled' && r.value && r.value.isSuccess !== false
        if (!ok) {
          failed = failed || (r.status === 'rejected' ? r.reason?.message : r.value?.message) || 'Please try again'
          return
        }
        const graph = r.value.data && r.value.data.insightsGraph
        // The API sends the figures as strings. Numbers for the chart; the values are the same.
        next[i] = ((graph && graph[prop]) || []).map(v => Number(v))
      })
      this.series = next
      this.error = failed
      this.loading = false
      this.$emit('loading', false)
    }
  }
}
</script>

<style scoped>
.og-metric { width: 190px; max-width: 46vw; }
.og-chart { position: relative; min-width: 0; overflow: hidden; }
.og-chart__veil { position: absolute; inset: 0; background: rgba(255, 255, 255, .7); display: flex; align-items: center; padding: 0 20%; }
.og-chart__veil .ins-skel { width: 100%; }
</style>
